import {
	createContext,
	forwardRef,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import {
	useForceRerender,
	useIsMounted,
	useResizeObserver
} from "client/Hooks";
import {
	isNil,
	isNotNil,
	setTrackingId,
	throttle
} from "client/Misc/utils";
import { range } from "client_legacy/ndaw/arrayHelpers";
import {
	hasImplicitDependencyOn,
	flattenChildren,
	mergeRefs,
	outOfContextCall,
	getReactNodeKey
} from "client/Misc/reactUtils";
import styles from "./CollapsibleList.module.less";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type HorizontalAlignment = "left" | "center" | "right";
export type VerticalAlignment = "top" | "center" | "bottom";
export type ItemResponsiveBehavior =
	| "collapse"
	| "always-visible"
	| "hide"
	| "always-collapsed";

export const horizontalAlignmentToStyles: {[key in HorizontalAlignment]: string | null} = {
	"left": null,
	"center": styles.root_alignCenter,
	"right": styles.root_alignRight
};

export const verticalAlignmentToStyles: {[key in VerticalAlignment]: string | null} = {
	"top": null,
	"center": styles.root_alignVerticalCenter,
	"bottom": styles.root_alignVerticalBottom
};

export type CollapsibleListProps = {
	moreButton: () => ReactElement;
	trackingId: string;
	moreButtonWrapperRef?: Ref<HTMLLIElement>;
	onInvisibleNodesChanged?(items: CollapsibleListChildWithKeyAndIndex[]): void;
	horizontalAlignment?: HorizontalAlignment;
	verticalAlignment?: VerticalAlignment;
	shouldOnlyMeasure?: boolean;
	onMaxContentWidthChange?(maxContentWidth: number): void;
	shouldPushMoreButtonRight?: boolean;
	alwaysShowMoreButton?: boolean;
	hideOverView?: boolean;
	height?: "auto" | "fill";
	tabbableIndex?: number;
};

export const CollapsibleList = (props: PropsWithChildren<CollapsibleListProps>): ReactElement => {
	const {
		moreButton,
		moreButtonWrapperRef,
		onInvisibleNodesChanged,
		horizontalAlignment = "left",
		verticalAlignment = "top",
		trackingId,
		shouldOnlyMeasure = false,
		onMaxContentWidthChange,
		shouldPushMoreButtonRight = false,
		alwaysShowMoreButton = false,
		hideOverView = true,
		height = "auto",
		children,
		tabbableIndex
	} = props;

	const childrenArray = useMemo(
		(): readonly ReactNode[] => {
			return flattenChildren(children);
		},
		[children]
	);

	const didRenderAfterAllMeasurementsRef: RefObject<boolean> = useRef<boolean>(false);
	const [moreButtonWidth, setMoreButtonWidth] = useState<number>(0);
	const ref: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const { width } = useResizeObserver(ref);
	const itemStatesRef: RefObject<CollapsibleListItemState[]> = useRef<CollapsibleListItemState[]>([]);

	const [instantlyNotifyAboutItemStateUpdate, itemStatesVersion] = useForceRerender();
	const getIsMounted: (() => boolean) = useIsMounted();

	const throttledNotifyAboutItemStateUpdate = useMemo(
		(): (() => void) => {
			return throttle(
				(): void => {
					if (getIsMounted()) {
						instantlyNotifyAboutItemStateUpdate();
					}
				},
				20
			);
		},
		[getIsMounted, instantlyNotifyAboutItemStateUpdate]
	);

	const notifyAboutItemStateUpdate = useCallback(
		(): void => {
			const isWidthOfEveryItemMeasured = (): boolean => {
				if (itemStatesRef.current.length < childrenArray.length) {
					return false;
				}

				return itemStatesRef.current
					.every(x => x.isWidthMeasured);
			};

			let didNotifyAboutUpdates: boolean = false;
			if (isWidthOfEveryItemMeasured()) {
				if (!didRenderAfterAllMeasurementsRef.current) {
					instantlyNotifyAboutItemStateUpdate();
					didRenderAfterAllMeasurementsRef.current = true;
					didNotifyAboutUpdates = true;
				}
				if (isNotNil(onMaxContentWidthChange)) {
					const hasAlwaysCollapsedItem: boolean = itemStatesRef.current
						.some(i => i.responsiveBehavior === "always-collapsed");

					let maxContentWidth: number = itemStatesRef.current
						.filter(i => i.responsiveBehavior !== "always-collapsed")
						.reduce((acc, cur) => acc + cur.width, 0);

					if (hasAlwaysCollapsedItem) {
						maxContentWidth += moreButtonWidth;
					}

					onMaxContentWidthChange(maxContentWidth);
				}
			}
			if (!didNotifyAboutUpdates) {
				try {
					throttledNotifyAboutItemStateUpdate();
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					throw e;
				}
			}
		},
		[
			childrenArray.length,
			instantlyNotifyAboutItemStateUpdate,
			onMaxContentWidthChange,
			throttledNotifyAboutItemStateUpdate,
			moreButtonWidth
		]
	);

	const ensureIndexInTheStateWithoutUpdateTrigger = useCallback(
		(index: number): void => {
			if (itemStatesRef.current.length <= index) {
				for (let i = itemStatesRef.current.length; i < index + 1; i++) {
					itemStatesRef.current.push({
						...defaultListItemState
					});
				}
			}
		},
		[]
	);

	useEffect(
		(): void => {
			if (itemStatesRef.current.length > childrenArray.length) {
				itemStatesRef.current.length = childrenArray.length;
				notifyAboutItemStateUpdate();
			} else if (itemStatesRef.current.length < childrenArray.length) {
				ensureIndexInTheStateWithoutUpdateTrigger(childrenArray.length - 1);
				notifyAboutItemStateUpdate();
			}
		},
		[
			childrenArray.length,
			ensureIndexInTheStateWithoutUpdateTrigger,
			notifyAboutItemStateUpdate
		]
	);

	const widthOfAlwaysVisibleItems = useMemo(
		(): number => {
			hasImplicitDependencyOn(itemStatesVersion);

			return itemStatesRef.current
				.filter(i => i.responsiveBehavior === "always-visible")
				.reduce((acc, cur) => acc + cur.width, 0);
		},
		[itemStatesVersion]
	);

	const shouldRenderMoreButton = useMemo(
		(): boolean => {
			hasImplicitDependencyOn(itemStatesVersion);
			if (alwaysShowMoreButton) {
				return true;
			}

			const hasAlwaysCollapsedItem: boolean = itemStatesRef.current
				.some(x => x.responsiveBehavior === "always-collapsed");

			if (hasAlwaysCollapsedItem) {
				return true;
			}

			if (shouldOnlyMeasure) {
				return false;
			}

			const totalWidthOfAllItems: number = itemStatesRef.current
				.filter(x => x.responsiveBehavior !== "hide")
				.reduce((acc, cur) => acc + cur.width, 0);

			return totalWidthOfAllItems > width;
		},
		[
			itemStatesVersion,
			shouldOnlyMeasure,
			width,
			alwaysShowMoreButton
		]
	);

	const visibleIndexes = useMemo(
		(): Set<number> => {
			hasImplicitDependencyOn(itemStatesVersion);

			if (shouldOnlyMeasure) {
				return new Set();
			}
			const itemStates: CollapsibleListItemState[] = itemStatesRef.current;
			let availableWidth: number = width - widthOfAlwaysVisibleItems;

			if (shouldRenderMoreButton) {
				availableWidth -= moreButtonWidth;
			}

			const indexes: number[] = range(childrenArray.length);
			indexes.sort(
				(a: number, b: number): number => {
					const aPrio: number = itemStates[a]?.priority ?? 0;
					const bPrio: number = itemStates[b]?.priority ?? 0;

					const prioBasedResult: number = bPrio - aPrio;

					if (prioBasedResult === 0) {
						const indexBasedResult: number = a - b;
						return indexBasedResult;
					}

					return prioBasedResult;
				}
			);

			const result: Set<number> = new Set();

			for (const index of indexes) {
				const item: CollapsibleListItemState | undefined = itemStates[index];
				const itemWidth: number = item?.width ?? 0;
				const isAlwaysVisible: boolean = item?.responsiveBehavior === "always-visible";
				const isAlwaysCollapsed: boolean = item?.responsiveBehavior === "always-collapsed";

				if (!isAlwaysCollapsed) {
					if (!isAlwaysVisible) {
						availableWidth -= itemWidth;
					}

					if (isAlwaysVisible || availableWidth >= 0) {
						result.add(index);
					}
				}
			}

			return result;
		},
		[
			childrenArray.length,
			itemStatesVersion,
			moreButtonWidth,
			shouldRenderMoreButton,
			width,
			widthOfAlwaysVisibleItems,
			shouldOnlyMeasure
		]
	);

	const nodes = useMemo(
		(): readonly CollapsibleListChildWithKeyAndIndex[] => {
			const result: CollapsibleListChildWithKeyAndIndex[] = [];

			for (let i = 0; i < childrenArray.length; i++) {
				const child: ReactNode = childrenArray[i];
				const shouldRenderInCollapsedMode: boolean = itemStatesRef.current[i]
					?.responsiveBehavior !== "hide";

				const node: CollapsibleListChildWithKeyAndIndex = {
					child,
					key: getReactNodeKey(child, i),
					originalIndex: i,
					shouldRenderInCollapsedMode,
					visibility: visibleIndexes.has(i)
						? "normal"
						: "collapsed"
				};

				result.push(node);
			}

			return result;
		},
		[childrenArray, visibleIndexes]
	);

	useEffect(
		(): void => {
			if (!onInvisibleNodesChanged) {
				return;
			}

			const invisibleNodes: CollapsibleListChildWithKeyAndIndex[] = nodes.filter(
				n => n.visibility === "collapsed"
			);
			onInvisibleNodesChanged(invisibleNodes);
		},
		[
			nodes,
			onInvisibleNodesChanged
		]
	);

	const effectiveTabbableIndex = useMemo(
		(): number | undefined => {
			if (isNil(tabbableIndex)) {
				return undefined;
			}

			if (visibleIndexes.has(tabbableIndex)) {
				return tabbableIndex;
			}

			const firstVisibleNode: CollapsibleListChildWithKeyAndIndex | undefined = nodes.find(
				(node: CollapsibleListChildWithKeyAndIndex): boolean => {
					const state: CollapsibleListItemState | undefined = itemStatesRef.current[node.originalIndex];
					const isZeroWidthItem: boolean = (state?.isWidthMeasured === true && state?.width === 0);

					return node.visibility === "normal" && !isZeroWidthItem;
				}
			);

			return firstVisibleNode?.originalIndex;
		},
		[tabbableIndex, visibleIndexes, nodes]
	);

	const areOnlyAlwaysVisibleItemsVisible = useMemo(
		(): boolean => {
			const itemStates: CollapsibleListItemState[] = itemStatesRef.current;
			for (const i of visibleIndexes) {
				const isAlwaysVisible: boolean = itemStates[i]?.responsiveBehavior === "always-visible";
				if (!isAlwaysVisible) {
					return false;
				}
			}

			return true;
		},
		[visibleIndexes]
	);

	const setWidthHandler = useCallback(
		(index: number, newWidth: number, isWidthMeasured: boolean): void => {
			const itemState: CollapsibleListItemState = itemStatesRef.current[index];

			const oldWidth: number = itemState.width;
			const wasWidthMeasured: boolean = itemState.isWidthMeasured;

			itemState.width = newWidth;
			itemState.isWidthMeasured = isWidthMeasured;

			const hasChanges: boolean = wasWidthMeasured !== isWidthMeasured
				|| oldWidth !== newWidth;

			if (hasChanges) {
				notifyAboutItemStateUpdate();
			}
		},
		[notifyAboutItemStateUpdate]
	);

	const widthChangeHandler = useCallback(
		(index: number, newWidth: number): void => {
			ensureIndexInTheStateWithoutUpdateTrigger(index);
			setWidthHandler(index, newWidth, true);
		},
		[
			ensureIndexInTheStateWithoutUpdateTrigger,
			setWidthHandler
		]
	);

	const resetWidthHandler = useCallback(
		(index: number): void => {
			if (itemStatesRef.current.length > index) {
				setWidthHandler(
					index,
					defaultListItemState.width,
					defaultListItemState.isWidthMeasured
				);
			}
		},
		[setWidthHandler]
	);

	const setPriorityHandler = useCallback(
		(index: number, newPriority: number): void => {
			const itemState: CollapsibleListItemState = itemStatesRef.current[index];

			const oldPriority: number = itemState.priority;
			itemState.priority = newPriority;

			const hasChanges: boolean = oldPriority !== newPriority;

			if (hasChanges) {
				notifyAboutItemStateUpdate();
			}
		},
		[notifyAboutItemStateUpdate]
	);

	const priorityChangeHandler = useCallback(
		(index: number, newPriority: number): void => {
			ensureIndexInTheStateWithoutUpdateTrigger(index);
			setPriorityHandler(index, newPriority);
		},
		[ensureIndexInTheStateWithoutUpdateTrigger, setPriorityHandler]
	);

	const resetPriorityHandler = useCallback(
		(index: number): void => {
			if (itemStatesRef.current.length > index) {
				setPriorityHandler(index, defaultListItemState.priority);
			}
		},
		[setPriorityHandler]
	);

	const setResponsiveBehaviorHandler = useCallback(
		(index: number, newResponsiveBehavior: ItemResponsiveBehavior): void => {
			const itemState: CollapsibleListItemState = itemStatesRef.current[index];
			const oldResponsiveBehavior: ItemResponsiveBehavior = itemState.responsiveBehavior;

			itemState.responsiveBehavior = newResponsiveBehavior;

			const hasChanges: boolean = oldResponsiveBehavior !== newResponsiveBehavior;

			if (hasChanges) {
				notifyAboutItemStateUpdate();
			}
		},
		[notifyAboutItemStateUpdate]
	);

	const responsiveBehaviorChangeHandler = useCallback(
		(index: number, newResponsiveBehavior: ItemResponsiveBehavior): void => {
			ensureIndexInTheStateWithoutUpdateTrigger(index);
			setResponsiveBehaviorHandler(index, newResponsiveBehavior);
		},
		[ensureIndexInTheStateWithoutUpdateTrigger, setResponsiveBehaviorHandler]
	);

	const resetResponsiveBehaviorHandler = useCallback(
		(index: number): void => {
			if (itemStatesRef.current.length > index) {
				setResponsiveBehaviorHandler(index, defaultListItemState.responsiveBehavior);
			}
		},
		[setResponsiveBehaviorHandler]
	);

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				horizontalAlignmentToStyles[horizontalAlignment],
				verticalAlignmentToStyles[verticalAlignment],
				{
					[styles.root_hideOverView]: hideOverView,
					[styles.root_fillHeight]: height === "fill"
				}
			)}
			ref={ref}
		>
			{nodes.map(
				(x) => (
					<Item
						key={x.key}
						index={x.originalIndex}
						tabIndex={isNotNil(effectiveTabbableIndex) && effectiveTabbableIndex === x.originalIndex
							? 0
							: -1
						}
						shouldShrink={areOnlyAlwaysVisibleItemsVisible}
						onWidthChange={widthChangeHandler}
						resetWidth={resetWidthHandler}
						onPriorityChange={priorityChangeHandler}
						resetPriority={resetPriorityHandler}
						onResponsiveBehaviorChange={responsiveBehaviorChangeHandler}
						resetResponsiveBehavior={resetResponsiveBehaviorHandler}
						isForMeasurement={x.visibility === "collapsed"}
					>
						{x.child}
					</Item>
				)
			)}
			<MoreButtonWrapperItem
				onWidthChange={setMoreButtonWidth}
				ref={moreButtonWrapperRef}
				shouldPushRight={shouldPushMoreButtonRight}
				isForMeasurement={!shouldRenderMoreButton}
			>
				{moreButton()}
			</MoreButtonWrapperItem>
		</ul>
	);
};
CollapsibleList.displayName = "CollapsibleList";

export type CollapsibleListItemState = {
	width: number;
	isWidthMeasured: boolean;
	priority: number;
	responsiveBehavior: ItemResponsiveBehavior;
};

const defaultListItemState: CollapsibleListItemState = {
	width: 0,
	isWidthMeasured: false,
	priority: 0,
	responsiveBehavior: "collapse"
};

export enum CollapsibleListItemRenderMode {
	Normal = 0,
	Collapsed = 1
}

type CollapsibleListItemContextCallbacks = {
	onPriorityChange(priority: number): void;
	resetPriority(): void;
	onResponsiveBehaviorChange(newResponsiveBehavior: ItemResponsiveBehavior): void;
	resetResponsiveBehavior(): void;
	onIsSpaceBetweenChange(value: boolean): void;
	resetIsSpaceBetween(): void;
};

export type CollapsibleListItemContextData = {
	renderMode: CollapsibleListItemRenderMode;
	index: number;
	tabIndex: number;
} & CollapsibleListItemContextCallbacks;

export const CollapsibleListItemContext = createContext<CollapsibleListItemContextData>({
	index: 0,
	tabIndex: -1,
	renderMode: CollapsibleListItemRenderMode.Normal,
	onPriorityChange: outOfContextCall("CollapsibleListItemContext", "onPriorityChange"),
	resetPriority: outOfContextCall("CollapsibleListItemContext", "resetPriority"),
	onResponsiveBehaviorChange: outOfContextCall("CollapsibleListItemContext", "onResponsiveBehaviorChange"),
	resetResponsiveBehavior: outOfContextCall("CollapsibleListItemContext", "resetResponsiveBehavior"),
	onIsSpaceBetweenChange: outOfContextCall("CollapsibleListItemContext", "onIsSpaceBetweenChange"),
	resetIsSpaceBetween: outOfContextCall("CollapsibleListItemContext", "resetIsSpaceBetween")
});

type ItemProps = {
	index: number;
	shouldShrink: boolean;
	isForMeasurement: boolean;
	onWidthChange(index: number, newWidth: number): void;
	resetWidth(index: number): void;
	onPriorityChange(index: number, newPriority: number): void;
	resetPriority(index: number): void;
	onResponsiveBehaviorChange(index: number, newCollapseBehavior: ItemResponsiveBehavior): void;
	resetResponsiveBehavior(index: number): void;
	tabIndex: number;
};

const Item = forwardRef<HTMLLIElement, PropsWithChildren<ItemProps>>(
	(props: PropsWithChildren<ItemProps>, forwardedRef: Ref<HTMLLIElement>): ReactElement => {
		const {
			index,
			shouldShrink,
			isForMeasurement,
			onWidthChange,
			onPriorityChange,
			onResponsiveBehaviorChange,
			resetWidth,
			resetPriority,
			resetResponsiveBehavior,
			children,
			tabIndex
		} = props;

		const [isSpaceBetween, setIsSpaceBetween] = useState<boolean>(false);
		const measurementRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
		const { width, wasInitialized } = useResizeObserver(measurementRef);
		const ref: Ref<HTMLLIElement> = mergeRefs(forwardedRef, measurementRef);

		useEffect(
			(): EffectWithDestructorResult => {
				if (wasInitialized) {
					if (isSpaceBetween) {
						onWidthChange(index, 0);
						return () => resetWidth(index);
					} else {
						onWidthChange(index, width);
						return () => resetWidth(index);
					}
				}

				return undefined;
			},
			[
				onWidthChange,
				resetWidth,
				wasInitialized,
				width,
				index,
				isSpaceBetween
			]
		);

		const contextData = useMemo(
			(): CollapsibleListItemContextData => {
				return {
					tabIndex,
					renderMode: CollapsibleListItemRenderMode.Normal,
					index: isForMeasurement ? -1 : index,
					onPriorityChange: (priority) => onPriorityChange(index, priority),
					resetPriority: () => resetPriority(index),
					onResponsiveBehaviorChange: (value) => onResponsiveBehaviorChange(index, value),
					resetResponsiveBehavior: () => resetResponsiveBehavior(index),
					onIsSpaceBetweenChange: setIsSpaceBetween,
					resetIsSpaceBetween: () => setIsSpaceBetween(false)
				};
			},
			[
				index,
				tabIndex,
				isForMeasurement,
				onPriorityChange,
				onResponsiveBehaviorChange,
				resetPriority,
				resetResponsiveBehavior
			]
		);

		return (
			<li
				ref={ref}
				className={classNames(
					styles.item,
					{
						[styles.item_alwaysVisible]: shouldShrink,
						[styles.item_measurement]: isForMeasurement,
						[styles.item_spaceBetween]: isSpaceBetween
					}
				)}
				aria-hidden={isForMeasurement}
			>
				<CollapsibleListItemContext.Provider
					value={contextData}
				>
					{children}
				</CollapsibleListItemContext.Provider>
			</li>
		);
	}
);
Item.displayName = "Item";

type MoreButtonWrapperProps = {
	onWidthChange(newWidth: number): void;
	isForMeasurement?: boolean;
	shouldPushRight?: boolean;
};

const MoreButtonWrapperItem = forwardRef<HTMLLIElement, PropsWithChildren<MoreButtonWrapperProps>>(
	(props: PropsWithChildren<MoreButtonWrapperProps>, forwardedRef: Ref<HTMLLIElement>): ReactElement => {
		const {
			onWidthChange,
			isForMeasurement = false,
			shouldPushRight = false,
			children
		} = props;

		const measurementRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
		const { width, wasInitialized } = useResizeObserver(measurementRef);
		const ref: Ref<HTMLLIElement> = mergeRefs(forwardedRef, measurementRef);

		useEffect(
			() => {
				if (wasInitialized) {
					onWidthChange(width);
				}
			},
			[onWidthChange, wasInitialized, width]
		);

		return (
			<li
				ref={ref}
				className={classNames(
					{
						[styles.item_pushRight]: shouldPushRight,
						[styles.item_measurement]: isForMeasurement
					}
				)}
				aria-hidden={isForMeasurement}
			>
				{children}
			</li>
		);
	}
);

MoreButtonWrapperItem.displayName = "MoreButtonWrapperItem";


export type CollapsibleListChildWithKeyAndIndex = {
	child: ReactNode;
	key: string;
	originalIndex: number;
	visibility: "normal" | "collapsed";
	shouldRenderInCollapsedMode: boolean;
};
